import type { DocumentTemplateVersionFetchFailedAction } from '@Application/Documents/Templates/Actions/documentTemplateVersionFetchFailed';
import FetchDocumentTemplateVersion from '@Application/Documents/Templates/Queries/FetchDocumentTemplateVersion';
import fetchDocumentTemplateVersion from '@Application/Documents/Templates/fetchDocumentTemplateVersion';
import documentTemplateHistorySidebarSignal, {
	closeDocumentTemplateHistorySidebar,
} from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTemplateHistorySidebarSignal';
import DocumentTemplateVersionRow from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateHistorySidebar/DocumentTemplateVersionRow';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Sidebar, Spinner } from '@verclam/ui-kit';

const versionListStyle = css`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const loadingRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

/**
 * Lists a template's versions, newest first, the current one marked "En vigueur". Clicking a row
 * toggles its declaration open, dispatching `fetchDocumentTemplateVersion` the first time it opens;
 * `selectedVersion` in the store is not scoped to a template, so it is only trusted once its own `id`
 * matches the row currently expanded.
 */
const DocumentTemplateHistorySidebar: React.FC = () => {
	const dispatch = useDispatch();
	const templateId = useSignalValue(documentTemplateHistorySidebarSignal);
	const { currentOrganization } = useCurrentOrganization();
	const templates = useSelector((state) => state.documentTemplatesStore.templates);
	const versions = useSelector((state) => state.documentTemplatesStore.templateVersions);
	const selectedVersion = useSelector((state) => state.documentTemplatesStore.selectedVersion);

	const [expandedVersionId, setExpandedVersionId] = React.useState<number | null>(null);
	const [isLoadingDeclaration, setIsLoadingDeclaration] = React.useState(false);
	const [declarationError, setDeclarationError] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (templateId === null) {
			setExpandedVersionId(null);
			setDeclarationError(null);
		}
	}, [templateId]);

	const template = React.useMemo(() => {
		return templates?.findById(templateId) ?? null;
	}, [templates, templateId]);

	const templateVersions = React.useMemo(() => {
		if (templateId === null || versions === null) {
			return [];
		}

		return versions.forTemplate(templateId);
	}, [versions, templateId]);

	const handleVersionToggle = React.useCallback(
		(versionId: number) => {
			setExpandedVersionId((current) => (current === versionId ? null : versionId));
			setDeclarationError(null);

			if (expandedVersionId === versionId) {
				return;
			}

			if (currentOrganization === null) {
				return;
			}

			setIsLoadingDeclaration(true);

			dispatch(
				fetchDocumentTemplateVersion(
					new FetchDocumentTemplateVersion(currentOrganization.id, versionId),
				),
			)
				.catch((action: DocumentTemplateVersionFetchFailedAction) => {
					setDeclarationError(action.payload?.message ?? 'Impossible de charger cette version.');
				})
				.finally(() => setIsLoadingDeclaration(false));
		},
		[expandedVersionId, currentOrganization, dispatch],
	);

	return (
		<Sidebar
			visible={templateId !== null}
			onHide={closeDocumentTemplateHistorySidebar}
			position="right"
			header="Historique des versions"
			blockScroll
		>
			{templates === null || versions === null ? (
				<div css={loadingRowStyle}>
					<Spinner aria-label="Chargement de l'historique" />
					<span>Chargement…</span>
				</div>
			) : (
				<ul css={versionListStyle}>
					{templateVersions.map((version) => (
						<DocumentTemplateVersionRow
							key={version.id}
							version={version}
							isCurrent={template !== null && template.currentVersionId === version.id}
							isExpanded={expandedVersionId === version.id}
							isLoadingDeclaration={isLoadingDeclaration && expandedVersionId === version.id}
							declarationError={expandedVersionId === version.id ? declarationError : null}
							declaration={
								selectedVersion !== null && selectedVersion.id === version.id
									? selectedVersion
									: null
							}
							onToggle={handleVersionToggle}
						/>
					))}
				</ul>
			)}
		</Sidebar>
	);
};

export default DocumentTemplateHistorySidebar;
